import { XProtectVersions, ServerSettingsData, HuskyIvoModels } from 'lib/types'
import { useState, useEffect } from 'react'
import {
  getOptionListsFromIvoModelChange,
  getOptionListsFromFormFactorChange,
  getOptionListsFromRaidTypeChange,
  getOptionListsFromRaidTypeChangeWithAutoSelect,
  getOptionListsFromIvoModelChangeWithAutoSelect,
  getOptionListsFromFormFactorChangeWithAutoSelect,
  isAutoSelectValue,
  HuskyConfigurationService,
  type OptionObject
} from './HuskyServerService'
import { checkNumberOfCamerasExceedingManagementServerSelection } from '../ServerSettingsService'

type HuskyServerSettingsDataSettings = {
  xProtectPlatforms: { label: string; value: string | number }[]
  huskyXProtectVersion: number
  huskyAddManagementServer: boolean
  huskyXProtectVersionCameraMaxQty
  ivoModels: { label: string; value: string | number }[]
  huskyIvoModel: string
  formFactors: { [key: string]: string }
  huskyFormFactor: string
  raidTypes: { [key: string]: string }
  huskyRaidType: string
  huskyMaxCamerasPerServer: string //Should be a number - forced to be a string
  huskyConcurrentSmartStreams: string //Should be a number - forced to be a string
  totalUsageSetup: any
  huskyTotalUsage: number
  handleHuskyTotalUsageChange
  netStorageSetup: any
  huskyNetStorage: number
  serverDiskQuantities: { [key: string]: string }
  huskyServerDiskQuantity: number
  supportedCameras: { [key: string]: string }
  huskySupportedCamera: number
  handleHuskyNetStorageChange
  handleHuskyXProtectPlatformChange
  handleHuskyValueChange
  handleHuskySupportedCamerasValueChange
  isNumberOfCamerasExceedingManagementServerSelection
}

export const useHuskyServerSettingsData = (
  formData,
  serverSettings: ServerSettingsData,
  camerasAmount: number,
  handleXProtectPlatformChange,
  handleValueChange,
  handleSupportedCamerasValueChange
): HuskyServerSettingsDataSettings => {
  const convertIvoModelsToOptionObject = (ivoModels: { label: string; value: string | number }[] | null): OptionObject => {
    return (
      ivoModels?.reduce((acc, item) => {
        acc[item.value.toString()] = item.label
        return acc
      }, {} as OptionObject) ?? {}
    )
  }

  const convertIvoModelsFromOptionObject = (optionObject: OptionObject): { label: string; value: string | number }[] => {
    return Object.entries(optionObject).map(([key, label]) => ({
      label: label,
      value: parseInt(key, 10)
    }))
  }

  const createIvoModelsList = (huskyIvoModels: HuskyIvoModels): { label: string; value: string | number }[] => {
    return Object.entries(huskyIvoModels).map(([key, model]) => ({
      label: model.modelName,
      value: parseInt(key, 10)
    }))
  }
  const [huskyServerSettings, setHuskyServerSettings] = useState(serverSettings?.huskyServerSettings)
  const huskyFormData = formData
  const huskyXProtectVersionsData: XProtectVersions = huskyFormData?.xProtectVersions ?? {}
  let xProtectPlatforms: { label: string; value: string | number }[] = Object.entries(huskyXProtectVersionsData)
    .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
    .map(([key, version]) => ({
      label: version.name,
      value: key
    }))

  const huskyAddManagementServer = huskyServerSettings?.addManagementServer
  const huskyXProtectVersion = huskyServerSettings?.xProtectVersion ?? 4 //initial value should be the XProtect Professional+ == 4
  const huskyXProtectVersionCameraMaxQty = huskyXProtectVersionsData[huskyXProtectVersion]?.camQtyMax

  const totalUsageSetup = huskyFormData?.totalUsageSetup ?? {}
  const netStorageSetup = huskyFormData?.netStorageSetup ?? {}

  const [ivoModels, setIvoModelList] = useState(null)
  const [huskyIvoModel, setHuskyIvoModel] = useState(huskyServerSettings?.ivoModel?.toString())

  const [formFactors, setFormFactors] = useState(huskyFormData?.formFactors)
  const [huskyFormFactor, setHuskyFormFactor] = useState(huskyServerSettings?.formFactor?.toString())

  const [raidTypes, setRaidTypes] = useState(huskyFormData?.raidTypes)
  const [huskyRaidType, setHuskyRaidType] = useState(huskyServerSettings?.advancedSettings?.liveDatabase?.raidType?.toString())

  const huskyMaxCamerasPerServer = huskyServerSettings?.mainSettings?.maxCamerasPerServer?.toString()
  const huskyConcurrentSmartStreams = huskyServerSettings?.mainSettings?.concurrentSmartClientStreams?.toString()

  const [huskyTotalUsage, setHuskyTotalUsage] = useState(huskyServerSettings?.maxSystemUtilization ?? huskyFormData?.totalUsageSetup?.value)
  const handleHuskyTotalUsageChange = propertyPath => (value: number) => {
    //update the rendered value in the component
    setHuskyTotalUsage(value)

    //update tthe server settings object
    handleValueChange(propertyPath, value)
  }

  const [huskyNetStorage, setHuskyNetStorage] = useState(huskyServerSettings?.maxDiskUtilization ?? huskyFormData?.netStorageSetup?.value)
  const handleHuskyNetStorageChange = propertyPath => (value: number) => {
    //update the rendered value in the component
    setHuskyNetStorage(value)

    //update tthe server settings object
    handleValueChange(propertyPath, value)
  }

  //if husky server settings have been updated then we set the range component values
  useEffect(() => {
    const currentSettings = serverSettings?.huskyServerSettings

    setHuskyServerSettings(currentSettings)
    setHuskyTotalUsage(currentSettings?.maxSystemUtilization)
    setHuskyNetStorage(currentSettings?.maxDiskUtilization)

    setHuskyIvoModel(currentSettings?.ivoModel?.toString())
    setHuskyFormFactor(currentSettings?.formFactor?.toString())
    setHuskyRaidType(currentSettings?.advancedSettings?.liveDatabase?.raidType?.toString())

    const ivoModelsList: HuskyIvoModels = huskyFormData?.huskyIvoModels ?? {}
    const ivoModelList = createIvoModelsList(ivoModelsList)
    const ivoModelsAsOptions = convertIvoModelsToOptionObject(ivoModelList)

    // Determine which configuration takes precedence and filter accordingly
    const ivoModel = currentSettings?.ivoModel
    const formFactor = currentSettings?.formFactor
    const raidType = currentSettings?.advancedSettings?.liveDatabase?.raidType

    if (ivoModel !== undefined && ivoModel !== 0) {
      // IVO Model takes precedence
      const filteredOptions = getOptionListsFromIvoModelChange(ivoModel, formFactor, huskyFormData?.formFactors, huskyFormData?.raidTypes)
      setIvoModelList(ivoModelList)
      setFormFactors(filteredOptions.formFactors || huskyFormData?.formFactors)
      setRaidTypes(filteredOptions.raidTypes || huskyFormData?.raidTypes)
    } else if (formFactor !== undefined && formFactor !== 0) {
      // Form Factor takes precedence
      const filteredOptions = getOptionListsFromFormFactorChange(formFactor, ivoModel, ivoModelsAsOptions, huskyFormData?.raidTypes)
      setIvoModelList(filteredOptions.ivoModels ? convertIvoModelsFromOptionObject(filteredOptions.ivoModels) : ivoModelList)
      setFormFactors(huskyFormData?.formFactors)
      setRaidTypes(filteredOptions.raidTypes || huskyFormData?.raidTypes)
    } else if (raidType !== undefined && raidType !== 0) {
      // RAID Type takes precedence
      const filteredOptions = getOptionListsFromRaidTypeChange(raidType, ivoModel, ivoModelsAsOptions, huskyFormData?.formFactors)
      setIvoModelList(filteredOptions.ivoModels ? convertIvoModelsFromOptionObject(filteredOptions.ivoModels) : ivoModelList)
      setFormFactors(filteredOptions.formFactors || huskyFormData?.formFactors)
      setRaidTypes(huskyFormData?.raidTypes)
    } else {
      // No specific configuration, use defaults
      setIvoModelList(ivoModelList)
      setFormFactors(huskyFormData?.formFactors)
      setRaidTypes(huskyFormData?.raidTypes)
    }
  }, [serverSettings, huskyFormData])

  const serverDiskQuantities = huskyFormData?.serverDiskQuantities ?? { key: '' }
  const huskyServerDiskQuantity = huskyServerSettings?.advancedSettings?.serverDiskQuantity ?? 0

  const supportedCameras = huskyFormData?.supportedCameras ?? { key: '' }
  const huskySupportedCamera = huskyServerSettings?.managementServerSettings?.supportedCameras ?? 0

  //Handles the XProtect Platform change and checks if we need to reinitialize the "add management server" property as well based on the XProtectplatform selection
  const handleHuskyXProtectPlatformChange = () => (xProtectPlatformId: number) => {
    let updatedServerSettings: ServerSettingsData = {
      ...serverSettings,
      huskyServerSettings: {
        ...huskyServerSettings,
        xProtectVersion: xProtectPlatformId
      }
    }

    //Check if we need to update the management server value if it is express+
    if (xProtectPlatformId === 3) {
      updatedServerSettings = {
        ...updatedServerSettings,
        huskyServerSettings: {
          ...updatedServerSettings.huskyServerSettings,
          addManagementServer: false
        }
      }
    }

    //check if the current max camera quantity is superior than the max camera quantity of the selected xProtect version.
    //if it is then we update the current max camera quantity value with the default max camera quantity value of the
    //xProtect version selection.
    const xProtectVersionCameraDefaultQty = huskyFormData?.xProtectVersions[xProtectPlatformId]?.camQtyDefault
    const xProtectVersionCameraMaxQty = huskyFormData?.xProtectVersions[xProtectPlatformId]?.camQtyMax
    const currentCameraMaxQuantity = huskyServerSettings?.mainSettings?.maxCamerasPerServer
    if (
      xProtectVersionCameraDefaultQty &&
      xProtectVersionCameraMaxQty &&
      ((xProtectPlatformId !== 3 && (currentCameraMaxQuantity < xProtectVersionCameraDefaultQty || currentCameraMaxQuantity > xProtectVersionCameraMaxQty)) ||
        (xProtectPlatformId === 3 && currentCameraMaxQuantity >= xProtectVersionCameraDefaultQty))
    ) {
      updatedServerSettings = {
        ...updatedServerSettings,
        huskyServerSettings: {
          ...updatedServerSettings.huskyServerSettings,
          mainSettings: {
            ...updatedServerSettings.huskyServerSettings.mainSettings,
            maxCamerasPerServer: xProtectVersionCameraDefaultQty
          }
        }
      }
    }

    handleXProtectPlatformChange(updatedServerSettings)
  }

  //Handling most of the element value change
  const handleHuskyValueChange = propertyPath => (value: boolean | string | number) => {
    handleValueChange(propertyPath, value)

    //update ivo model, form factor and raid array options based on the selection
    updateListOfOptions(propertyPath, value)
  }

  const updateListOfOptions = (propertyPath, value) => {
    if (propertyPath.includes('ivoModel') || propertyPath.includes('formFactor') || propertyPath.includes('raidType')) {
      if (propertyPath.includes('ivoModel')) {
        updateOptionsFromIvoModelChange(value)
      }

      if (propertyPath.includes('formFactor')) {
        updateOptionsFromFormFactorChange(value)
      }

      if (propertyPath.includes('raidType')) {
        updateOptionsFromRaidTypeChange(value)
      }
    }
  }

  const updateOptionsFromIvoModelChange = value => {
    const filteredOptions = getOptionListsFromIvoModelChangeWithAutoSelect(value, huskyFormFactor, huskyRaidType, huskyFormData?.formFactors, huskyFormData?.raidTypes)

    // Update form factors
    if (filteredOptions.formFactors) {
      setFormFactors(filteredOptions.formFactors)
      updateValueIfNeeded(huskyFormFactor, filteredOptions.formFactors, setHuskyFormFactor, ['huskyServerSettings', 'formFactor'])
    }

    // Update raid types
    if (filteredOptions.raidTypes) {
      setRaidTypes(filteredOptions.raidTypes)
      updateValueIfNeeded(huskyRaidType, filteredOptions.raidTypes, setHuskyRaidType, ['huskyServerSettings', 'advancedSettings', 'liveDatabase', 'raidType'])
    }
  }

  const updateOptionsFromFormFactorChange = value => {
    const ivoModelsAsOptions = convertIvoModelsToOptionObject(ivoModels)
    const filteredOptions = getOptionListsFromFormFactorChangeWithAutoSelect(value, huskyIvoModel, huskyRaidType, ivoModelsAsOptions, huskyFormData?.raidTypes)

    // Update IVO models
    if (filteredOptions.ivoModels) {
      const convertedIvoModels = convertIvoModelsFromOptionObject(filteredOptions.ivoModels)
      setIvoModelList(convertedIvoModels)
      updateValueIfNeeded(huskyIvoModel, filteredOptions.ivoModels, setHuskyIvoModel, ['huskyServerSettings', 'ivoModel'])
    }

    // Update raid types
    if (filteredOptions.raidTypes) {
      setRaidTypes(filteredOptions.raidTypes)
      updateValueIfNeeded(huskyRaidType, filteredOptions.raidTypes, setHuskyRaidType, ['huskyServerSettings', 'advancedSettings', 'liveDatabase', 'raidType'])
    }
  }

  const updateOptionsFromRaidTypeChange = value => {
    const ivoModelsAsOptions = convertIvoModelsToOptionObject(ivoModels)
    const filteredOptions = getOptionListsFromRaidTypeChangeWithAutoSelect(value, huskyIvoModel, huskyFormFactor, ivoModelsAsOptions, huskyFormData?.formFactors)

    // Update IVO models
    if (filteredOptions.ivoModels) {
      const convertedIvoModels = convertIvoModelsFromOptionObject(filteredOptions.ivoModels)
      setIvoModelList(convertedIvoModels)
      updateValueIfNeeded(huskyIvoModel, filteredOptions.ivoModels, setHuskyIvoModel, ['huskyServerSettings', 'ivoModel'])
    }

    // Update form factors
    if (filteredOptions.formFactors) {
      setFormFactors(filteredOptions.formFactors)
      updateValueIfNeeded(huskyFormFactor, filteredOptions.formFactors, setHuskyFormFactor, ['huskyServerSettings', 'formFactor'])
    }
  }

  // Simple utility to update value if current value is not valid and not AutoSelect
  const updateValueIfNeeded = (currentValue: string | undefined, validOptions: OptionObject, setter: (value: string) => void, valuePath: string[]) => {
    if (!(currentValue in validOptions) && !isAutoSelectValue(currentValue)) {
      const validKeys = Object.keys(validOptions)
        .map(Number)
        .filter(n => !isNaN(n))
      if (validKeys.length > 0) {
        const currentValueNum = currentValue ? Number(currentValue) : undefined
        const defaultOption = HuskyConfigurationService.getDefaultOption(validKeys, currentValueNum)
        setter(defaultOption.toString())
        handleValueChange(valuePath, defaultOption)
      }
    }
  }

  //Handling the management server
  useEffect(() => {
    const hasExceeded = checkNumberOfCamerasExceedingManagementServerSelection(
      serverSettings?.huskyServerSettings?.addManagementServer,
      serverSettings?.huskyServerSettings?.managementServerSettings?.supportedCameras,
      camerasAmount
    )
    setIsNumberOfCamerasExceedingManagementServerSelection(hasExceeded)
  }, [camerasAmount, serverSettings])

  const [isNumberOfCamerasExceedingManagementServerSelection, setIsNumberOfCamerasExceedingManagementServerSelection] = useState(false)
  const handleHuskySupportedCamerasValueChange = () => (value: number) => {
    let updatedHuskyServerSettings: ServerSettingsData = {
      ...serverSettings,
      huskyServerSettings: {
        ...huskyServerSettings,
        managementServerSettings: {
          supportedCameras: value
        }
      }
    }

    handleSupportedCamerasValueChange(updatedHuskyServerSettings)
    setIsNumberOfCamerasExceedingManagementServerSelection(
      checkNumberOfCamerasExceedingManagementServerSelection(
        updatedHuskyServerSettings?.huskyServerSettings?.addManagementServer,
        updatedHuskyServerSettings?.huskyServerSettings?.managementServerSettings?.supportedCameras,
        camerasAmount
      )
    )
  }
  //End of handling the management server

  return {
    xProtectPlatforms,
    huskyXProtectVersion,
    huskyAddManagementServer,
    huskyXProtectVersionCameraMaxQty,
    ivoModels,
    huskyIvoModel,
    formFactors,
    huskyFormFactor,
    raidTypes,
    huskyRaidType,
    huskyMaxCamerasPerServer,
    huskyConcurrentSmartStreams,
    totalUsageSetup,
    huskyTotalUsage,
    handleHuskyTotalUsageChange,
    netStorageSetup,
    huskyNetStorage,
    serverDiskQuantities,
    huskyServerDiskQuantity,
    supportedCameras,
    huskySupportedCamera,
    handleHuskyNetStorageChange,
    handleHuskyXProtectPlatformChange,
    handleHuskyValueChange,
    handleHuskySupportedCamerasValueChange,
    isNumberOfCamerasExceedingManagementServerSelection
  }
}
